import { useEffect, useRef, useState } from 'react';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { submitFeedback } from '@packages/api/endpoints/feedback.js';
import type { SubmitFeedbackInput, SubmitFeedbackResponse } from '@packages/types';
import { ALLOWED_FEEDBACK_MIME_TYPES, FeedbackTypeSchema } from '@packages/types';
import { Button } from '../ui/Button';
import { TextButton } from '../ui/TextLink';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import { FormDialog } from '../ui/FormDialog';

const MAX_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_ATTACHMENTS = 3;

const FeedbackFormSchema = z.object({
  message: z.string().min(10, 'Message must be at least 10 characters').max(2000, 'Message must be 2000 characters or less'),
  type: FeedbackTypeSchema.prefault('other'),
});

// zodResolver uses the schema input type (where .prefault() fields are optional)
// as TFieldValues. We use z.input here to match that, and z.infer (output) for
// the mutation which requires the post-validation shape with defaults applied.
type FeedbackFormValues = z.input<typeof FeedbackFormSchema>;
type FeedbackFormData = z.infer<typeof FeedbackFormSchema>;

interface AttachmentFile {
  file: File;
  base64: string;
  error?: string;
}

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

export function FeedbackDialog({ open, onClose }: FeedbackDialogProps) {
  const [submitted, setSubmitted] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isDragging, onDragEnter, onDragOver, onDragLeave, onDrop } = useDragAndDrop((files) => processFiles(files));

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FeedbackFormValues, unknown, FeedbackFormData>({
    resolver: zodResolver(FeedbackFormSchema),
    defaultValues: { type: 'other', message: '' },
  });

  const mutation = useMutation<SubmitFeedbackResponse, Error, SubmitFeedbackInput>({
    mutationFn: submitFeedback,
    onSuccess: () => {
      setSubmitted(true);
      successTimerRef.current = setTimeout(() => {
        successTimerRef.current = null;
        setSubmitted(false);
        reset();
        setAttachments([]);
        onClose();
      }, 2000);
    },
  });

  // Focus textarea when dialog opens
  useEffect(() => {
    if (!open) return;
    const timerId = setTimeout(() => {
      document.getElementById('feedback-message')?.focus();
    }, 50);
    return () => clearTimeout(timerId);
  }, [open]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current !== null) clearTimeout(successTimerRef.current);
    };
  }, []);

  function handleClose() {
    if (mutation.isPending) return;
    reset();
    setSubmitted(false);
    setAttachments([]);
    setAttachmentError(null);
    onClose();
  }

  async function processFiles(files: File[]): Promise<void> {
    setAttachmentError(null);

    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) {
      setAttachmentError(`Maximum ${MAX_ATTACHMENTS} attachments allowed.`);
      return;
    }

    const toProcess = files.slice(0, remaining);
    if (files.length > remaining) {
      setAttachmentError(`Only ${remaining} more file${remaining === 1 ? '' : 's'} can be added (maximum ${MAX_ATTACHMENTS}).`);
    }

    const newAttachments: AttachmentFile[] = [];

    for (const file of toProcess) {
      if (!ALLOWED_FEEDBACK_MIME_TYPES.includes(file.type as never)) {
        setAttachmentError(`"${file.name}" is not a supported file type. Allowed: images, PDF, text, CSV, JSON, YAML, Markdown.`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        setAttachmentError(`"${file.name}" exceeds the 5 MB limit (${(file.size / 1024 / 1024).toFixed(1)} MB).`);
        continue;
      }
      const base64 = await readAsBase64(file);
      newAttachments.push({ file, base64 });
    }

    setAttachments((prev) => [...prev, ...newAttachments]);
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    await processFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  }

  function removeAttachment(index: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  }

  function onSubmit(data: FeedbackFormData) {
    mutation.mutate({
      message: data.message,
      type: data.type,
      source: 'WEB_APP',
      attachments: attachments.map((a) => ({
        filename: a.file.name,
        mimeType: a.file.type,
        data: a.base64,
      })),
    });
  }

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title="Send feedback"
      description="Share a bug, idea, or anything on your mind."
      loading={mutation.isPending}
      maxWidth="max-w-md"
      footer={
        submitted ? undefined : (
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="feedback-form"
              loading={mutation.isPending}
            >
              Send feedback
            </Button>
          </div>
        )
      }
    >
      <div
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className="relative"
      >
        {isDragging && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-primary/90"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-12 w-12 text-white"
              aria-hidden="true"
            >
              <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
              <path d="M12 12v9" />
              <path d="m16 16-4-4-4 4" />
            </svg>
            <p className="mt-3 text-lg font-medium text-white">Drop files here</p>
          </div>
        )}

        {submitted ? (
          <div className="rounded-lg bg-success/10 p-4 text-center text-sm font-medium text-success">
            Thanks for your feedback!
          </div>
        ) : (
          <form id="feedback-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Select
              id="feedback-type"
              label="Type"
              {...register('type')}
              error={errors.type?.message}
            >
              <option value="other">Other</option>
              <option value="bug">Bug report</option>
              <option value="idea">Feature idea</option>
            </Select>

            <Textarea
              id="feedback-message"
              label="Message"
              placeholder="Describe what you experienced or what you'd like to see..."
              rows={5}
              {...register('message')}
              error={errors.message?.message}
            />

            {/* Attachments */}
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm font-medium text-secondary">
                  Attachments <span className="font-normal text-text-muted">({attachments.length}/{MAX_ATTACHMENTS})</span>
                </span>
                {attachments.length < MAX_ATTACHMENTS && (
                  <span className="text-xs text-text-muted">
                    <TextButton onClick={() => fileInputRef.current?.click()} tone="sentence">
                      + Add file
                    </TextButton>
                    {' · or drag and drop'}
                  </span>
                )}
              </div>

              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_FEEDBACK_MIME_TYPES.join(',')}
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />

                {attachments.length > 0 && (
                  <ul className="space-y-1.5">
                    {attachments.map((a, i) => (
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      <li key={i} className="flex items-center justify-between rounded-md border border-border bg-bg-muted px-3 py-1.5 text-sm">
                        <span className="truncate text-secondary">{a.file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(i)}
                          className="ml-2 shrink-0 text-text-muted hover:text-error"
                          aria-label={`Remove ${a.file.name}`}
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

              </div>

              {attachmentError && (
                <p className="mt-1.5 text-xs text-error">{attachmentError}</p>
              )}
            </div>

            {mutation.isError && (
              <p className="text-sm text-error">
                {mutation.error instanceof Error ? mutation.error.message : 'Failed to send feedback. Please try again.'}
              </p>
            )}

          </form>
        )}
      </div>
    </FormDialog>
  );
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data URL prefix (e.g. "data:image/png;base64,")
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
